<template>
  <div>
    <!-- PAGE HEADER -->
    <page-header
      title="The latest and greatest."
      subtitle="All the latest news and info about Art Grab and your favorite artists."
      bgImage="/img/headers/blog-header.webp"
    />

    <!-- BLOG SEARCH CTA -->
    <call-to-action-section text="Looking for something specific?">
      <div class="blog-search-wrapper">
        <base-input
          type="text"
          placeholder="Search the blog"
          v-model="searchQuery"
          @keydown.enter="onSearchEnter"
          spellcheck="false"
        />
        <router-link
          :to="'/blog?search=' + encodeURI(searchQuery)"
          class="btn btn-secondary"
          >SEARCH</router-link
        >
      </div>
    </call-to-action-section>

    <!-- BLOG POSTS LIST -->
    <section class="blog-wrapper">
      <div class="container">
        <div class="row">
          <div class="col-md-8 post-list">
            <!-- FILTER MESSAGES -->
            <div
              v-if="Object.keys(this.$route.query).length"
              class="filter-message mb-3"
            >
              <div v-if="this.$route.query.tag">
                Found {{ items.length }} post{{
                  items.length !== 1 ? "s" : ""
                }}
                tagged with "{{ this.$route.query.tag }}"
              </div>
              <div v-else-if="this.$route.query.author">
                Found {{ items.length }} post{{
                  items.length !== 1 ? "s" : ""
                }}
                by "{{ this.$route.query.author }}"
              </div>
              <div v-else-if="this.$route.query.search">
                Found {{ items.length }} post{{
                  items.length !== 1 ? "s" : ""
                }}
                containing "{{ this.$route.query.search }}"
              </div>
              <base-button
                size="sm"
                type="danger"
                @click="this.clearFilters"
                class="clear-filter-button"
              >
                <i class="fas fa-times"></i>Clear Filters
              </base-button>
            </div>

            <div
              class="post-content"
              v-for="item in items"
              v-bind:key="item.fields.postUrl"
            >
              <router-link :to="'/blog/' + item.fields.postUrl">
                <img
                  class="img-fluid"
                  :src="item.fields.blogHeader.fields.file.url"
                  :alt="item.fields.title"
                />
              </router-link>
              <div class="post-details">
                <blog-tags
                  class="post-tags"
                  v-if="item.fields.tags"
                  :tags="item.fields.tags"
                />
                <div class="date">{{ getDate(item.fields.date) }}</div>
              </div>
              <router-link :to="'/blog/' + item.fields.postUrl">
                <h2>{{ item.fields.title }}</h2>
              </router-link>
              <p>{{ item.fields.snippet }}</p>
              <router-link
                class="btn btn-sm btn-primary"
                :to="'/blog/' + item.fields.postUrl"
                >READ FULL ARTICLE</router-link
              >
            </div>
          </div>
          <div class="col-md-4 blog-sidebar">
            <blog-sidebar />
          </div>
        </div>
      </div>
    </section>

    <!-- FAQ CTA -->
    <call-to-action-section
      text="Have questions about how you can use licensed artwork? Check out our FAQ."
    >
      <router-link to="/about#faq" class="btn btn-secondary"
        >FREQUENTLY ASKED QUESTIONS</router-link
      >
    </call-to-action-section>

    <!-- NEWSLETTER SIGNUP -->
    <newsletter-signup bg-image="/img/headers/newsletter-bg-2.jpg" />
  </div>
</template>

<script>
import { mapState } from "vuex";
import {
  PageHeader,
  NewsletterSignup,
  CallToActionSection,
  BlogTags,
  BlogSidebar,
} from "../../components";
const artgrabURL = process.env.VUE_APP_ARTGRAB_URL;

export default {
  name: "blog-page",
  metaInfo() {
    return {
      title: "Blog - Art Grab",
      meta: [
        {
          vmid: "twitter:card",
          name: "twitter:card",
          content: `summary_large_image`,
        },
        {
          vmid: "twitter:title",
          name: "twitter:title",
          content: `Blog - Art Grab`,
        },
        {
          vmid: "twitter:description",
          name: "twitter:description",
          content: `All the latest news and info about Art Grab, your favorite artists, and customer projects.`,
        },
        {
          vmid: "twitter:image",
          name: "twitter:image",
          content: "https://artgrab.co/img/headers/blog-header.webp",
        },
        {
          vmid: "og:url",
          property: "og:url",
          content: `https://artgrab.co/blog`,
        },
        { vmid: "og:title", property: "og:title", content: `Blog - Art Grab` },
        {
          vmid: "og:description",
          property: "og:description",
          content: `All the latest news and info about Art Grab, your favorite artists, and customer projects.`,
        },
        {
          vmid: "og:image",
          property: "og:image",
          content: "https://artgrab.co/img/headers/blog-header.webp",
        },
        {
          vmid: "description",
          property: "description",
          content: `All the latest news and info about Art Grab, your favorite artists, and customer projects.`,
        },
      ],
      link: [{ rel: "canonical", href: `${artgrabURL}/blog` }],
    };
  },
  components: {
    PageHeader,
    NewsletterSignup,
    CallToActionSection,
    BlogTags,
    BlogSidebar,
  },
  data() {
    return {
      items: [],
      searchQuery: "",
      month: [
        "January",
        "February",
        "March",
        "April",
        "May",
        "June",
        "July",
        "August",
        "September",
        "October",
        "November",
        "December",
      ],
    };
  },
  methods: {
    onSearchEnter() {
      this.$router.push({ path: "/blog", query: { search: this.searchQuery } });
    },
    getDate(date) {
      const d = new Date(date);
      return `${
        this.month[d.getUTCMonth()]
      } ${d.getUTCDate()}, ${d.getUTCFullYear()}`;
    },
    getTags(posts) {
      let tags = [];

      posts.forEach((post) => {
        if (post.fields.tags.length > 0) {
          tags = [...tags, ...post.fields.tags];
        }
      });
      return [...new Set([...tags])];
    },
    clearFilters() {
      this.$router.push({ path: "/blog" });
    },
    getContent() {
      this.searchQuery = "";

      if (this.$route.query.tag) {
        this.$contentful
          .getEntries({
            order: "-sys.createdAt",
            content_type: "blogPost",
            "fields.tags": this.$route.query.tag,
          })
          .then((response) => {
            this.items = response.items;
          })
          .catch((err) => console.error(err));
      } else if (this.$route.query.author) {
        this.$contentful
          .getEntries({
            order: "-sys.createdAt",
            content_type: "blogPost",
            "fields.authorName": this.$route.query.author,
          })
          .then((response) => {
            this.items = response.items;
          })
          .catch((err) => console.error(err));
      } else if (this.$route.query.search) {
        this.$contentful
          .getEntries({
            order: "-sys.createdAt",
            content_type: "blogPost",
            query: this.$route.query.search,
          })
          .then((response) => {
            this.items = response.items;
          })
          .catch((err) => console.error(err));
      } else {
        this.$contentful
          .getEntries({ order: "-sys.createdAt", content_type: "blogPost" })
          .then((response) => {
            this.items = response.items;
          })
          .catch((err) => console.error(err));
      }
    },
  },
  mounted() {
    this.$store.commit("setLayout", "page-layout");
    this.getContent();
  },
  computed: {
    ...mapState(["blogTags"]),
  },
  watch: {
    $route: {
      handler() {
        this.getContent();
      },
    },
  },
};
</script>

<style lang="scss">
.blog-search-wrapper {
  display: flex;
  align-items: center;

  .form-group {
    margin-bottom: 0;
    .form-control {
      height: 43px;
      width: 390px;
      margin-right: 0.5rem;
      background: rgba(255, 255, 255, 0.2);
      color: white;

      &:focus {
        border-color: #11cdef;
      }
    }
  }
}

.blog-wrapper {
  padding: 4.5rem 1rem;

  .filter-message {
    display: flex;
    justify-content: space-between;
    align-items: center;
    background-color: #f7f7f7;
    padding: 0.5rem 1rem;
    border-radius: 3px;

    .clear-filter-button {
      margin-left: 0.5rem;
      i {
        font-size: 1rem;
        padding: 0;
        margin-right: 0.2rem;
      }
    }
  }

  .post-list {
    h2 {
      font-size: 1.5rem;
      margin-top: 1rem;
    }

    img {
      border-radius: 4px;
    }

    .post-content {
      margin-bottom: 5rem;
      /* &:not(:last-child) {

      } */

      .post-details {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-top: 1rem;

        .date {
          font-weight: 400;
          color: #999;
          white-space: nowrap;
        }
      }
    }
  }
}

@media (max-width: 575.98px) {
  .blog-search-wrapper {
    .form-group {
      .form-control {
        width: initial;
      }
    }
  }

  .blog-wrapper {
    .post-list {
      .post-content {
        .post-details {
          .post-tags {
            display: none;
          }

          .date {
            font-size: 0.875rem;
          }
        }
      }
    }
  }
}
</style>
